import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import PropertyCard from "@/components/PropertyCard";
import LoanProviderStrip from "@/components/LoanProviderStrip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, MapPinned, Loader2, SlidersHorizontal } from "lucide-react";

const listingTabs = ["all", "sale", "rent", "lease"];

export default function Browse() {
  const { t } = useLang();
  const { feature } = useSettings();
  const [params] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState("all");
  const [propType, setPropType] = useState("all");
  const [q, setQ] = useState(params.get("q") || "");
  const [pincode, setPincode] = useState("");
  const [coords, setCoords] = useState(null);
  const [radius, setRadius] = useState("100");
  const [gpsLoading, setGpsLoading] = useState(false);
  const navigate = useNavigate();
  const [propId, setPropId] = useState("");
  const lookupById = async () => {
    const code = propId.replace(/[^0-9]/g, "");
    if (!code) { toast.error("Enter a property ID"); return; }
    try {
      const { data } = await api.get("/property-lookup", { params: { code } });
      navigate(`/property/${data.id}`);
    } catch (e) { toast.error("No property found for that ID"); }
  };

  const load = async (extra = {}, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const p = { status: "available" };
      if (listing !== "all") p.listing_type = listing;
      if (propType !== "all") p.property_type = propType;
      if (q) p.q = q;
      if (pincode) p.pincode = pincode;
      if (extra.lat != null) { p.lat = extra.lat; p.lon = extra.lon; p.radius = radius; }
      const { data } = await api.get("/properties", { params: p });
      setProperties(data.properties);
    } catch (e) {
      if (!silent) toast.error("Failed to load properties");
    } finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { load(coords || {}); /* eslint-disable-next-line */ }, [listing, propType]);

  useEffect(() => { if (pincode || q) localStorage.setItem("mp_area", pincode || q); else localStorage.removeItem("mp_area"); }, [pincode, q]);

  // Real-time auto-refresh
  useEffect(() => {
    const iv = setInterval(() => load(coords || {}, true), 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [listing, propType, coords, q, pincode]);

  useEffect(() => {
    if (params.get("near") === "1") handleNearMe();
    // eslint-disable-next-line
  }, []);

  const handleNearMe = () => {
    if (!navigator.geolocation) { toast.error("Location not supported"); return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        setCoords(c);
        setGpsLoading(false);
        toast.success("Showing properties near you");
        load(c);
      },
      () => { setGpsLoading(false); toast.error("Could not get your location"); }
    );
  };

  const clearNear = () => { setCoords(null); load({}); };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <h1 className="font-heading font-bold text-3xl text-brand-ink tracking-tight mb-2">{t("nav.browse")}</h1>
      <p className="text-brand-muted mb-6">{t("featured.sub")}</p>

      {/* Search by Property ID — for customers who saw an offline ad */}
      {feature("search_by_id") && (
      <div className="bg-card border border-brand-line rounded-2xl p-4 mb-4 flex flex-col sm:flex-row gap-3 sm:items-end" data-testid="search-by-id-box">
        <div className="flex-1">
          <label className="text-xs font-medium text-brand-muted flex items-center gap-1 mb-1"><Search className="w-3 h-3" /> Search by Property ID</label>
          <Input value={propId} onChange={(e) => setPropId(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookupById()} placeholder="e.g. 18668341993" data-testid="search-by-id-input" />
        </div>
        <Button onClick={lookupById} className="rounded-full bg-brand-emerald hover:bg-brand-emeraldHover" data-testid="search-by-id-btn">Go to Property</Button>
      </div>
      )}

      {/* Listing type tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {listingTabs.map((lt) => (
          <button
            key={lt}
            onClick={() => setListing(lt)}
            data-testid={`filter-${lt}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${listing === lt ? "bg-brand-emerald text-white" : "bg-secondary text-brand-muted hover:bg-brand-emerald/10"}`}
          >
            {t(`filter.${lt}`)}
          </button>
        ))}
      </div>

      {/* Filters */}
      {feature("search_filters") && (
      <div className="bg-card border border-brand-line rounded-2xl p-4 mb-8 grid gap-3 md:grid-cols-[1fr_1fr_160px_auto] items-end" data-testid="search-filters-section">
        <div>
          <label className="text-xs font-medium text-brand-muted flex items-center gap-1 mb-1"><Search className="w-3 h-3" /> {t("hero.search")}</label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(coords || {})} placeholder="Area / City / Title" data-testid="search-q" />
        </div>
        <div>
          <label className="text-xs font-medium text-brand-muted mb-1 block">{t("form.pincode")}</label>
          <Input value={pincode} onChange={(e) => setPincode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(coords || {})} placeholder="e.g. 520010" data-testid="search-pincode" />
        </div>
        <div>
          <label className="text-xs font-medium text-brand-muted mb-1 block flex items-center gap-1"><SlidersHorizontal className="w-3 h-3" /> {t("filter.type")}</label>
          <Select value={propType} onValueChange={setPropType}>
            <SelectTrigger data-testid="filter-proptype"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("filter.all")}</SelectItem>
              <SelectItem value="house">House</SelectItem>
              <SelectItem value="villa">Villa</SelectItem>
              <SelectItem value="apartment">Apartment</SelectItem>
              <SelectItem value="land">Land / Plot</SelectItem>
              <SelectItem value="commercial">Commercial</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => load(coords || {})} className="rounded-full bg-brand-emerald hover:bg-brand-emeraldHover" data-testid="search-apply">
          {t("filter.search")}
        </Button>
      </div>
      )}

      {/* Near me */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {!coords ? (
          <Button onClick={handleNearMe} variant="outline" className="rounded-full" data-testid="near-me-btn">
            {gpsLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <MapPinned className="w-4 h-4 mr-1.5" />}
            {t("filter.nearme")}
          </Button>
        ) : (
          <>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-brand-muted">{t("filter.radius")}</span>
              <Select value={radius} onValueChange={(v) => { setRadius(v); }}>
                <SelectTrigger className="w-24 h-9" data-testid="radius-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["10", "25", "50", "100"].map((r) => <SelectItem key={r} value={r}>{r} km</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" className="rounded-full bg-brand-emerald" onClick={() => load(coords)}>Apply</Button>
            </div>
            <Button variant="ghost" size="sm" onClick={clearNear} className="text-brand-terracotta">Clear location</Button>
          </>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-emerald" /></div>
      ) : properties.length === 0 ? (
        <div className="text-center py-20 text-brand-muted" data-testid="no-results">{t("noResults")}</div>
      ) : (
        <>
        <div className="-mx-5 sm:-mx-8 mb-6"><LoanProviderStrip /></div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="property-grid">
          {properties.map((p) => <PropertyCard key={p.id} property={p} />)}
        </div>
        </>
      )}
    </div>
  );
}
